<template>
  <div class="p-6">
    <div class="bg-white rounded-lg border border-mid-neutral-50 overflow-hidden">
      <div class="px-6 py-4 border-b border-mid-neutral-50">
        <div class="flex items-center justify-between">
          <div class="grid grid-cols-1">
            <input
              id="search-users"
              v-model="searchQuery"
              type="text"
              name="search-users"
              class="col-start-1 row-start-1 block w-80 rounded-md bg-white py-2 pr-10 pl-3 text-base text-dark-neutral-500 outline-1 -outline-offset-1 outline-mid-neutral-50 placeholder:text-mid-neutral-200 focus:outline-2 focus:-outline-offset-2 focus:outline-primary-500 sm:pr-9 sm:text-sm/6"
              placeholder="Search for users..."
            />
            <SearchIcon
              class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-gray-400 sm:size-4"
              aria-hidden="true"
            />
          </div>
          <div class="flex gap-2">
            <Switch v-model="showDisabledOnly" class="text-sm font-normal text-dark-neutral-300">
              Show inactive users
            </Switch>
            <InviteUserFloatingMenu />
            <CreateUsersFloatingMenu />
          </div>
        </div>
      </div>
      <div class="overflow-hidden">
        <table class="w-full divide-y divide-slate-200" aria-describedby="users table">
          <thead class="bg-white w-full">
            <tr>
              <th scope="col" class="py-3.5 pl-[24px] text-left text-sm font-medium text-gray-900">
                Username
              </th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-medium text-gray-900">
                Email
              </th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-medium text-gray-900">
                <UserRolesModal />
              </th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-medium text-gray-900">
                Status
              </th>
              <th scope="col" class="pr-[24px] py-3.5 text-right text-sm font-medium text-gray-900">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200 bg-white">
            <tr
              v-for="user in paginatedUsers"
              :key="'user-' + user.id"
              :class="user.isDisabled ? 'bg-gray-100' : 'hover:bg-light-neutral-200'"
            >
              <td class="py-3 px-6 pr-[24px]">
                <div class="flex gap-2 items-center">
                  <div
                    :class="[
                      'size-10 flex items-center justify-center rounded-full font-medium ring-2 ',
                      user.isDisabled
                        ? 'bg-gray-200 text-gray-400 ring-gray-200'
                        : 'bg-primary-50 text-primary-500 ring-white',
                    ]"
                  >
                    <p class="text-sm font-medium">
                      {{ (user.firstName.charAt(0) + user.lastName.charAt(0)).toUpperCase() }}
                    </p>
                  </div>
                  <div class="flex gap-1">
                    <p
                      :class="
                        user.isDisabled ? 'text-gray-300' : 'text-gray-700 text-sm font-medium'
                      "
                    >
                      {{ buildUserFullname(user) }}
                    </p>
                  </div>
                </div>
              </td>
              <td class="py-3 px-3">
                <div>
                  <p
                    :class="user.isDisabled ? 'text-gray-300' : 'text-gray-700 text-sm font-medium'"
                  >
                    {{ user.email }}
                  </p>
                </div>
              </td>
              <td class="py-3 px-3">
                <p :class="user.isDisabled ? 'text-gray-300' : 'text-gray-700 text-sm font-medium'">
                  {{
                    user.role === Role.ADMIN
                      ? "Admin"
                      : user.role === Role.USER
                        ? "Reviewer"
                        : "Reader"
                  }}
                </p>
              </td>
              <td class="py-3 px-3">
                <div class="flex gap-2 items-center">
                  <div
                    :class="user.isDisabled ? 'text-gray-300' : 'text-green-600'"
                    class="text-sm"
                  >
                    {{ user.isDisabled ? "Inactive" : "Active" }}
                  </div>

                  <FloatingMenu>
                    <template #activator="{ show }">
                      <HeadlessSwitch
                        :model-value="!user.isDisabled"
                        :class="[
                          !user.isDisabled ? 'bg-green-600' : 'bg-light-neutral-600',
                          'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-green-600 focus:ring-offset-2',
                        ]"
                        @click="show"
                      >
                        <span
                          aria-hidden="true"
                          :class="[
                            !user.isDisabled ? 'translate-x-5' : 'translate-x-0',
                            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out',
                          ]"
                        />
                      </HeadlessSwitch>
                    </template>
                    <template #content="{ hide }">
                      <div class="p-4">
                        <div class="rounded-lg p-4">
                          <div class="flex">
                            <div class="flex-1 md:flex md:justify-between">
                              <p v-if="!user.isDisabled" class="text-md text-dark-neutral-500">
                                Are you sure you want to disable this user?<br />
                              </p>
                              <p v-else class="text-md text-dark-neutral-500">
                                Are you sure you want to enable this user?
                              </p>
                            </div>
                          </div>
                        </div>
                        <div class="flex justify-end gap-2 mt-4">
                          <button
                            class="w-24 inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-50"
                            @click="hide"
                          >
                            Cancel
                          </button>
                          <button
                            :class="[
                              'inline-flex w-24 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-xs',
                              user.isDisabled
                                ? 'bg-green-600 hover:bg-green-500'
                                : 'bg-red-600 hover:bg-red-500',
                            ]"
                            @click="toggleUserState(user.id!, hide)"
                          >
                            {{ user.isDisabled ? "Enable" : "Disable" }}
                          </button>
                        </div>
                      </div>
                    </template>
                  </FloatingMenu>
                </div>
              </td>
              <td class="py-3 pr-[24px]">
                <div class="flex-end gap-2 text-right">
                  <button
                    class="inline-flex items-center gap-x-1 text-sm font-semibold text-gray-900"
                  >
                    <div
                      class="bg-light-neutral-500 hover:bg-light-neutral-600 rounded-full h-[30px] w-[30px] flex items-center justify-center text-mid-neutral-500"
                    >
                      <EditUserFloatingMenu
                        :data="{
                          firstName: user.firstName,
                          lastName: user.lastName,
                          role: user.role,
                          userId: user.id!,
                        }"
                      />
                    </div>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="filteredUsers.length === 0" class="w-full flex items-center justify-center py-10">
        <p class="text-dark-neutral-500">No users matched your search.</p>
      </div>
      <div
        v-if="filteredUsers.length > 0"
        class="flex items-center justify-between px-6 py-4 border-t border-mid-neutral-50 sticky bottom-0 bg-white z-1"
      >
        <div>
          <p class="text-base font-medium text-neutral-text-title">
            Total Users: {{ filteredUsers.length }}
          </p>
          <p
            v-if="users.quota.value && users.quota.value.limit !== 0"
            class="text-sm text-gray-400"
          >
            Quota: {{ users.quota.value.usage }} / {{ users.quota.value.limit }}
          </p>
        </div>
        <Pagination
          v-model:page="currentPage"
          v-model:items-per-page="itemsPerPage"
          :total-items="filteredUsers.length"
          items-label="Users"
        />
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { computed, ref } from "vue";
import { Role } from "@core/domain/types/role.type";
import EditUserFloatingMenu from "./EditUserFloatingMenu.vue";
import { Switch as HeadlessSwitch } from "@headlessui/vue";
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";
import useUsers from "@app/composables/use-users";
import useSnackbar from "@app/composables/use-snackbar";
import useLoading from "@app/composables/use-loading";
import { SnackbarState } from "@app/types";
import { HttpException } from "@core/exceptions/http.exception";
import { errorMessage } from "@app/utils/error-message";
import { User } from "@core/domain/models/user.model";
import UserRolesModal from "./UserRolesModal.vue";
import Pagination from "@app/components/Global/Pagination/Pagination.vue";
import SearchIcon from "@app/components/Icons/SearchIcon.vue";
import CreateUsersFloatingMenu from "./CreateUsersFloatingMenu.vue";
import InviteUserFloatingMenu from "./InviteUserFloatingMenu.vue";
import Switch from "@app/components/Global/Switch.vue";

const snackbar = useSnackbar();
const loading = useLoading();
const users = useUsers();
users.refresh();
users.fetchQuota();
const itemsPerPage = ref(5);
const currentPage = ref(1);
const searchQuery = ref("");
const showDisabledOnly = ref(false);

const filteredUsers = computed(() => {
  return users.users.value.filter((user) => {
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const query = searchQuery.value.toLowerCase();
    const matchesSearch = fullName.includes(query) || user.email.toLowerCase().includes(query);
    const matchesDisabledFilter = showDisabledOnly.value ? user.isDisabled : !user.isDisabled;
    return matchesSearch && matchesDisabledFilter;
  });
});

const paginatedUsers = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredUsers.value.slice(start, end);
});

async function toggleUserState(userId: string, cb: () => void) {
  const user = users.users.value.find((u) => u.id === userId);
  try {
    loading.start();
    if (!user?.isDisabled) {
      await users.disableUser(userId);
      snackbar.success("Success");
    } else {
      await users.enableUser(userId);
      snackbar.success("Success");
    }
    cb();
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
  } finally {
    loading.stop();
  }
}

function buildUserFullname(user: User) {
  if (!user) return "";
  let fullName = "";
  if (user.firstName) {
    fullName += user.firstName;
  }
  if (user.lastName) {
    fullName += " " + user.lastName;
  }
  if (!fullName) {
    fullName = user.email;
  }
  return fullName;
}
</script>
